import { createDraftSafeSelector } from '@reduxjs/toolkit'
import { OrderBook, Security } from '../proto/generated/security'
import { SecurityStateType, Snapshot } from '../reducer/security.reducer'
import store, { RootState } from '../store'
import { isValidExchange } from '../util/security'
import { formatGreeks, greekColumns } from '../pages/home/optionChainViewTabs/components/DynamicColumnRender'
import { Exchange } from '../proto/generated/common'

const state = (state: RootState) => state

export const selectOverviewSecurity = createDraftSafeSelector(state, (state: RootState) => {
    const { overviewSecurity } = state.securityStore
    return overviewSecurity
})

export const selectExpressionData = createDraftSafeSelector(state, (state: RootState) => state.securityStore.expression)
export const selectExpressionSecurities = createDraftSafeSelector(state, (state: RootState): Record<string, Security> => {
    const data: Record<string, Security> = {};
    const { expression: { expressionSymbols }, securities } = state.securityStore
    return expressionSymbols.reduce((acc: Record<string, Security>, key: string) => {
        acc[key] = securities[key]
        return acc
    }, data)
})

export const selectExpressionSnapshots = createDraftSafeSelector(state, (state: RootState): Record<string, Snapshot> => {
    const { expression: { expressionSymbols }, snapshots } = state.securityStore
    const result: Record<string, Snapshot> = {}
    return expressionSymbols.reduce((acc: Record<string, Snapshot>, key: string) => {
        acc[key] = snapshots[key];
        return acc
    }, result)
})

export const selectUserSecurityExpressions = createDraftSafeSelector(state, (state: RootState) => {
    const { userExpressions } = state.securityStore
    return userExpressions
})
export const selectSecuritiesLoaded = createDraftSafeSelector(state, (state: RootState): Boolean => state.securityStore.securitiesLoaded)
export const selectPriceMultiplier = createDraftSafeSelector(state, (state: RootState) => state.securityStore?.securities?.["NSE:NIFTY_50"]?.priceMultiplier??100 as number)
export const selectLastRefreshTime = createDraftSafeSelector(state, (state: RootState): SecurityStateType['snapshots'] => state.securityStore.lastRefreshTime)
export const selectSnapshots = createDraftSafeSelector(state, (state: RootState): SecurityStateType['snapshots'] => state.securityStore.snapshots)
export const selectSnapshot = (key: string) => createDraftSafeSelector(state, (state: RootState): Snapshot => state.securityStore.snapshots[key])
export const selectGreeks = createDraftSafeSelector(state, (state: RootState): SecurityStateType['greeks'] => state.securityStore.greeks)
export const selectOrderBooks = createDraftSafeSelector(state, (state: RootState): SecurityStateType['orderBooks'] => state.securityStore.orderBooks)
export const selectOrderBook = (key: string) => createDraftSafeSelector(state, (state: RootState): OrderBook | undefined => state.securityStore.orderBooks[key])
export const selectOrderBookTopBid = (key: string) => createDraftSafeSelector(state, (state: RootState): number | undefined => state.securityStore.candleChangeBook?.[key]?.topBid)
export const selectOrderBookTopAsk = (key: string) => createDraftSafeSelector(state, (state: RootState): number | undefined => state.securityStore.candleChangeBook?.[key]?.topAsk)
export const selectCandleChangeLtp = (key: string) => createDraftSafeSelector(state, (state: RootState): number | undefined => state.securityStore.candleChangeBook?.[key]?.ltp)
export const selectCandleChangeLtpChange = (key: string) => createDraftSafeSelector(state, (state: RootState): number | undefined => state.securityStore.candleChangeBook?.[key]?.ltpChange)
export const selectCandleChangeLtpChangePercent = (key: string) => createDraftSafeSelector(state, (state: RootState): number | undefined => state.securityStore.candleChangeBook?.[key]?.ltpChangePercent)
export const selectCandleChangeOpenInterest = (key: string) => createDraftSafeSelector(state, (state: RootState): number | undefined => state.securityStore.candleChangeBook?.[key]?.openInterest)
export const selectCandleChangeOpenInterestChange = (key: string) => createDraftSafeSelector(state, (state: RootState): number | undefined => state.securityStore.candleChangeBook?.[key]?.oiChange)
export const selectReferenceSecurityMap = createDraftSafeSelector(state, (state: RootState): SecurityStateType['referenceSecurityMap'] => state.securityStore.referenceSecurityMap)
export const selectCandleChangeIv = (key: string) => createDraftSafeSelector(state, (state: RootState): number | undefined => Number(state.securityStore.candleChangeBook?.[key]?.impliedVolatility?.toFixed(2)))
export const selectCandleChangeField = (key: string, field: string) => createDraftSafeSelector(state, (state: RootState): number | string | undefined => {
    if(greekColumns.includes(field)) {
        if(field === 'gamma') return state.securityStore.candleChangeBook?.[key]?.optionGreeks?.[field]?.toFixed(4)
        return formatGreeks(state.securityStore.candleChangeBook?.[key]?.optionGreeks?.[field])
    }
    return state.securityStore.candleChangeBook?.[key]?.[field]
})
export const selectExchangesByCompanyCode = (companyCode: string) => createDraftSafeSelector(state, (state: RootState): Exchange[] => {
        const exchanges = window.securityManager.getAllSecurities()
            .filter(sec => sec.companyCode === companyCode)
            .map(sec => sec.exchange)
            .filter(isValidExchange)
        return Array.from(new Set(exchanges))
})

export const getSecuritySnapshot = (key: string): Snapshot | undefined => {
    const state = store.getState()
    return state?.securityStore?.snapshots[key]
}

export const getOrderBook = (key: string): OrderBook | undefined => {
    const state = store.getState()
    return state?.securityStore?.orderBooks?.[key]
}